// File: /src/config/llmProviders.js
// Updated: July 2026 - Structured pricing + cost reporting metadata (issue #71)
// Rates verified against provider pricing pages on 2026-07-25; the
// gemini-3.8-flash entry was verified on 2026-09-05.
//
// Pricing is stored as NUMBERS (USD per 1M tokens) in `rates`, not prose.
// The display strings the settings dropdown shows are derived from those
// numbers by `formatModelRate`, so the figure we quote to the user and the
// figure we bill an estimate against can never drift apart.
//
// `costReporting` records how honest a per-generation cost figure can be:
//   "actual"    — the provider returns real money in its response body.
//   "estimated" — we only get token counts, so cost is rates × tokens.
//   "free"      — runs locally, nothing to bill.
// Only OpenRouter reports actual spend against the same key the user pastes
// in; OpenAI and Anthropic gate cost behind an organisation admin key, which
// is not something a fitness-app user should ever have to mint.
//
// The API prices shared-credit generations from its own copy of the models and
// rates (api/lib/llmCatalogue.json). After changing either here, run
// `npm run export:llm-catalogue`; llmCatalogue.test.js fails until you do.

import { formatPrice } from "../utils/money";

export const LLM_PROVIDERS = {
  ollama: {
    label: "Ollama (Local)",
    isLocal: true,
    apiKeyRequired: false,
    group: "recommended",
    costReporting: "free",
    endpoint: "/api", // Proxy endpoint
    models: [
      // Will be populated dynamically from API
      "llama3.2:latest",
      "llama3.1:latest",
      "mistral:latest",
      "gemma2:9b",
      "qwen2.5:7b",
      "phi3:latest",
    ],
    dynamicModels: true, // Fetch models from API
    defaultModel: "llama3.2:latest",
    description: "Run models locally on your machine — no API cost at all",
    setupGuide: "https://ollama.ai/download",
  },

  openrouter: {
    label: "OpenRouter",
    isLocal: false,
    apiKeyRequired: true,
    group: "recommended",
    // The only provider that returns real spend per request (usage.cost) with
    // an ordinary inference key — no admin/organisation key needed.
    costReporting: "actual",
    // OpenAI-compatible chat completions — one key, every vendor.
    endpoint: "https://openrouter.ai/api/v1",
    // A curated shortlist of popular models. OpenRouter carries hundreds more;
    // model ids follow `vendor/model` and can be checked at
    // https://openrouter.ai/models
    models: [
      "google/gemini-3.1-flash-lite", // Cheapest capable option — the default
      "google/gemini-3.8-flash", // Latest Gemini Flash
      "google/gemini-3.7-flash", // Previous Gemini Flash
      "google/gemini-3.6-flash", // Older Gemini Flash
      "anthropic/claude-opus-5.5", // Anthropic flagship
      "anthropic/claude-sonnet-5.5", // Anthropic balanced
      "anthropic/claude-haiku-5.5", // Anthropic fast and cheap
      "openai/gpt-6-astra", // OpenAI flagship
      "openai/gpt-6.1-sol", // OpenAI balanced
      "openai/gpt-6-luna", // OpenAI fast and cheap
      "x-ai/grok-4.5", // xAI flagship
      "deepseek/deepseek-v4-flash", // Cheapest on this list
      "deepseek/deepseek-v4-pro", // Strong reasoning, very low cost
      "qwen/qwen3.7-plus", // Qwen workhorse
      "z-ai/glm-5.2", // GLM flagship
      "moonshotai/kimi-k3", // Long-context agentic model
      "minimax/minimax-m3", // MiniMax flagship via OpenRouter
      "meta-llama/llama-4-maverick", // Open-weights option
    ],
    defaultModel: "google/gemini-3.1-flash-lite",
    description:
      "One API key for models from every major vendor — and the only provider that reports the exact cost of each generation",
    setupGuide: "https://openrouter.ai/keys",
    rates: {
      "google/gemini-3.1-flash-lite": { input: 0.25, output: 1.5 },
      // OpenRouter is discounting the Gemini Flash models against Google's
      // list price; the standard rate is recorded so the pre-flight estimate
      // does not under-quote when the discount lapses. Actual spend comes back
      // per request anyway, so this only affects the estimate.
      "google/gemini-3.8-flash": { input: 1.5, output: 7.5 },
      "google/gemini-3.7-flash": { input: 1.5, output: 7.5 },
      "google/gemini-3.6-flash": { input: 1.5, output: 7.5 },
      // Anthropic and OpenAI rates checked against OpenRouter's catalogue
      // on 2026-10-09; they match the vendors' own list prices.
      "anthropic/claude-opus-5.5": { input: 4.0, output: 20.0 },
      "anthropic/claude-sonnet-5.5": { input: 2.0, output: 10.0 },
      "anthropic/claude-haiku-5.5": { input: 0.1, output: 0.5 },
      "openai/gpt-6-astra": { input: 10.0, output: 50.0 },
      "openai/gpt-6.1-sol": { input: 2.0, output: 10.0 },
      "openai/gpt-6-luna": { input: 0.1, output: 0.5 },
      "x-ai/grok-4.5": { input: 2.0, output: 6.0 },
      "deepseek/deepseek-v4-flash": { input: 0.1, output: 0.2 },
      "deepseek/deepseek-v4-pro": { input: 0.44, output: 0.87 },
      "qwen/qwen3.7-plus": { input: 0.32, output: 1.28 },
      "z-ai/glm-5.2": { input: 0.79, output: 2.49 },
      "moonshotai/kimi-k3": { input: 3.0, output: 15.0 },
      "minimax/minimax-m3": { input: 0.3, output: 1.2 },
      "meta-llama/llama-4-maverick": { input: 0.2, output: 0.8 },
    },
    features: [
      "json_mode",
      "function_calling",
      "long_context",
      "multi_vendor",
      "automatic_fallback",
      "cost_reporting",
    ],
    contextWindow: {
      "google/gemini-3.1-flash-lite": 1048576,
      "google/gemini-3.8-flash": 1048576,
      "google/gemini-3.7-flash": 1048576,
      "google/gemini-3.6-flash": 1048576,
      "anthropic/claude-opus-5.5": 1000000,
      "anthropic/claude-sonnet-5.5": 1000000,
      "anthropic/claude-haiku-5.5": 1000000,
      "openai/gpt-6-astra": 1050000,
      "openai/gpt-6.1-sol": 1050000,
      "openai/gpt-6-luna": 1050000,
      "x-ai/grok-4.5": 500000,
      "deepseek/deepseek-v4-flash": 1048576,
      "deepseek/deepseek-v4-pro": 1048576,
      "qwen/qwen3.7-plus": 1000000,
      "z-ai/glm-5.2": 1048576,
      "moonshotai/kimi-k3": 1048576,
      "minimax/minimax-m3": 1048576,
      "meta-llama/llama-4-maverick": 1048576,
    },
    specialFeatures: {
      "anthropic/claude-opus-5.5": ["reasoning", "tool_use"],
      "anthropic/claude-sonnet-5.5": ["reasoning", "tool_use"],
      "anthropic/claude-haiku-5.5": ["reasoning", "tool_use"],
      "openai/gpt-6-astra": ["reasoning", "tool_use"],
      "openai/gpt-6.1-sol": ["reasoning", "tool_use"],
      "openai/gpt-6-luna": ["reasoning", "tool_use"],
      "google/gemini-3.8-flash": ["thinking", "long_context"],
      "google/gemini-3.7-flash": ["thinking", "long_context"],
      "google/gemini-3.6-flash": ["thinking", "long_context"],
      "deepseek/deepseek-v4-pro": ["reasoning", "cost_efficient"],
    },
  },

  google: {
    label: "Google (Gemini)",
    isLocal: false,
    apiKeyRequired: true,
    group: "recommended",
    // Only usageMetadata token counts come back; billing lives in Cloud
    // Billing. Kept in the recommended group regardless because its free tier
    // is the most generous on offer, and routing Gemini via OpenRouter BYOK
    // does NOT inherit that free tier.
    costReporting: "estimated",
    endpoint: "https://generativelanguage.googleapis.com/v1beta",
    models: [
      "gemini-3.8-flash", // Latest Flash generation — the default
      "gemini-3.7-flash", // Previous Flash generation
      "gemini-3.6-flash", // Older Flash generation
      "gemini-3.5-flash", // Most intelligent for sustained agentic and coding work
      "gemini-3.5-flash-lite", // Cheap 3.5 lite
      "gemini-3.1-pro", // Improved reasoning (preview)
      "gemini-3.1-flash-lite", // Cheapest overall
      "gemini-2.5-pro", // Previous-generation thinking model
      "gemini-2.5-flash", // Previous generation
      "gemini-2.5-flash-lite", // Previous generation, most cost-effective
      // gemini-2.0-flash shut down 1 June 2026 and 1.5-pro is long gone; both
      // removed. gemini-3-flash-preview is off the pricing page too.
    ],
    // Defaults to the current Flash generation. It is the model Google points
    // new work at, it has a free tier of its own, and it is the one tuned for
    // long-horizon agentic work — which is what plan generation is.
    // gemini-3.1-flash-lite stays selectable for anyone who would rather have
    // the cheapest per-token rate and the free tier's request headroom.
    defaultModel: "gemini-3.8-flash",
    description:
      "Gemini 3.x family with native thinking — the most generous free tier of any provider here",
    rates: {
      // Standard rates. The 3.6/3.7/3.8 Flash models are all on introductory
      // pricing of $0.75 / $3.75 until 31 Dec 2026, so estimates run
      // conservative (high) until then rather than under-quoting from January.
      "gemini-3.8-flash": { input: 1.5, output: 7.5 },
      "gemini-3.7-flash": { input: 1.5, output: 7.5 },
      "gemini-3.6-flash": { input: 1.5, output: 7.5 },
      "gemini-3.5-flash": { input: 1.5, output: 9.0 },
      "gemini-3.5-flash-lite": { input: 0.3, output: 2.5 },
      // Base (≤200K context) tier; the >200K tier is $4.00 / $18.00.
      "gemini-3.1-pro": { input: 2.0, output: 12.0 },
      // Text/image/video input; audio input is $0.50. The output rate here was
      // previously recorded as $0.30 — five times under the real $1.50, on the
      // model this app defaults to.
      "gemini-3.1-flash-lite": { input: 0.25, output: 1.5 },
      // Base (≤200K context) tier; the >200K tier is $2.50 / $15.00.
      "gemini-2.5-pro": { input: 1.25, output: 10.0 },
      "gemini-2.5-flash": { input: 0.3, output: 2.5 },
      "gemini-2.5-flash-lite": { input: 0.1, output: 0.4 },
    },
    // Shown alongside the paid rate so the free allowance stays discoverable.
    // Google publishes free-tier availability per model but the per-model
    // request caps move, so we don't quote specific RPM/RPD numbers here.
    freeTier: {
      "gemini-3.8-flash": "free tier available",
      "gemini-3.7-flash": "free tier available",
      "gemini-3.6-flash": "free tier available",
      "gemini-3.5-flash": "free tier available",
      "gemini-3.5-flash-lite": "free tier available",
      "gemini-3.1-flash-lite": "free tier available",
      "gemini-2.5-pro": "free tier available",
      "gemini-2.5-flash": "free tier available",
      "gemini-2.5-flash-lite": "free tier available",
    },
    rateNotes: {
      "gemini-3.8-flash": "intro $0.75/$3.75 until 31 Dec 2026",
      "gemini-3.7-flash": "intro $0.75/$3.75 until 31 Dec 2026",
      "gemini-3.6-flash": "intro $0.75/$3.75 until 31 Dec 2026",
      "gemini-3.1-pro": "≤200K context; higher above",
      "gemini-2.5-pro": "≤200K context; higher above",
    },
    features: [
      "json_mode",
      "vision",
      "long_context",
      "thinking",
      "grounding",
      "batch_mode",
      "free_tier",
    ],
    contextWindow: {
      "gemini-3.8-flash": 1048576, // 1M tokens
      "gemini-3.7-flash": 1048576, // 1M tokens
      "gemini-3.6-flash": 1048576, // 1M tokens
      "gemini-3.5-flash": 1048576, // 1M tokens
      "gemini-3.5-flash-lite": 1048576, // 1M tokens
      "gemini-3.1-pro": 1000000, // 1M tokens
      "gemini-3.1-flash-lite": 1000000, // 1M tokens
      "gemini-2.5-pro": 1000000, // 1M tokens
      "gemini-2.5-flash": 1000000, // 1M tokens
      "gemini-2.5-flash-lite": 1000000, // 1M tokens
    },
    specialFeatures: {
      "gemini-3.8-flash": [
        "thinking_level",
        "thought_preservation",
        "agentic",
        "long_context",
      ],
      "gemini-3.7-flash": [
        "thinking_level",
        "thought_preservation",
        "agentic",
        "long_context",
      ],
      "gemini-3.6-flash": [
        "thinking_level",
        "thought_preservation",
        "agentic",
        "long_context",
      ],
      "gemini-3.5-flash": [
        "thinking_level",
        "thought_preservation",
        "agentic",
        "long_context",
      ],
      "gemini-3.5-flash-lite": ["cost_optimized", "high_throughput"],
      "gemini-3.1-pro": [
        "thinking_budget",
        "advanced_reasoning",
        "custom_tools",
      ],
      "gemini-3.1-flash-lite": [
        "cost_optimized",
        "high_throughput",
        "thinking_budget",
      ],
      "gemini-2.5-pro": ["thinking_budget", "advanced_reasoning"],
      "gemini-2.5-flash": ["thinking_budget", "fast_inference"],
      "gemini-2.5-flash-lite": ["cost_optimized", "high_throughput"],
    },
  },

  openai: {
    label: "OpenAI",
    isLocal: false,
    apiKeyRequired: true,
    group: "more",
    // Chat completions return token counts only. Real cost needs the
    // organisation Admin API (/v1/organization/costs) and an admin key.
    costReporting: "estimated",
    endpoint: "https://api.openai.com/v1",
    // OpenAI's three current flagships (2026-10-09). The GPT-5.x models were
    // dropped: each tier has a cheaper, newer GPT-6 equivalent. A saved
    // setting for one snaps to the default (resolveKnownModel).
    models: [
      "gpt-6-astra", // Flagship
      "gpt-6.1-sol", // Balanced
      "gpt-6-luna", // Fast and cheapest — the default
    ],
    defaultModel: "gpt-6-luna",
    description: "GPT-6 family with adjustable reasoning",
    // Verified on OpenAI's pricing page 2026-10-09.
    rates: {
      "gpt-6-astra": { input: 10.0, output: 50.0 },
      "gpt-6.1-sol": { input: 2.0, output: 10.0 },
      "gpt-6-luna": { input: 0.1, output: 0.5 },
    },
    features: [
      "json_mode",
      "function_calling",
      "reasoning",
      "vision",
      "thinking",
    ],
    contextWindow: {
      "gpt-6-astra": 1050000,
      "gpt-6.1-sol": 1050000,
      "gpt-6-luna": 1050000,
    },
  },

  anthropic: {
    label: "Anthropic (Claude)",
    isLocal: false,
    apiKeyRequired: true,
    group: "more",
    // Token counts only; cost requires the Admin API cost_report endpoint and
    // a separate organisation admin key.
    costReporting: "estimated",
    endpoint: "https://api.anthropic.com/v1",
    // Model IDs carry no date suffix — `claude-sonnet-5-5`, never
    // `claude-sonnet-5-5-20260xxx`. One model per tier, all 5.5 (2026-10-09);
    // older ones were dropped because each 5.5 costs the same or less. A saved
    // setting for one snaps to the default (resolveKnownModel).
    models: [
      "claude-opus-5-5", // Flagship
      "claude-sonnet-5-5", // Balanced — the default
      "claude-haiku-5-5", // Fastest and cheapest
    ],
    defaultModel: "claude-sonnet-5-5",
    description: "Claude 5.5 family with adaptive thinking and 1M context",
    // Verified on Anthropic's pricing page 2026-10-09. Haiku's rate is for
    // prompts up to 100K tokens; ours are ~3K.
    rates: {
      "claude-opus-5-5": { input: 4.0, output: 20.0 },
      "claude-sonnet-5-5": { input: 2.0, output: 10.0 },
      "claude-haiku-5-5": { input: 0.1, output: 0.5 },
    },
    features: [
      "long_context",
      "vision",
      "reasoning",
      "thinking",
      "tool_use",
      "batch_processing",
    ],
    contextWindow: {
      "claude-opus-5-5": 1000000,
      "claude-sonnet-5-5": 1000000,
      "claude-haiku-5-5": 1000000,
    },
    specialFeatures: {
      "claude-opus-5-5": ["adaptive_thinking", "long_context"],
      "claude-sonnet-5-5": ["adaptive_thinking", "long_context"],
      "claude-haiku-5-5": ["adaptive_thinking", "long_context"],
    },
  },

  deepseek: {
    label: "DeepSeek",
    isLocal: false,
    apiKeyRequired: true,
    group: "more",
    // No per-request cost, but DeepSeek does expose GET /user/balance against
    // the same key — the only non-OpenRouter provider here that reports money.
    costReporting: "estimated",
    balanceEndpoint: "https://api.deepseek.com/user/balance",
    endpoint: "https://api.deepseek.com/v1",
    // `deepseek-chat` and `deepseek-reasoner` were deprecated on
    // 2026-07-24 — they were the only two models this list carried.
    models: [
      "deepseek-v4-flash", // General purpose — the default
      "deepseek-v4-pro", // Stronger reasoning
    ],
    defaultModel: "deepseek-v4-flash",
    description:
      "Open-source models with exceptional reasoning and cost efficiency",
    // Input rates are the cache-miss price; cache hits bill lower.
    rates: {
      "deepseek-v4-flash": { input: 0.14, output: 0.28 },
      "deepseek-v4-pro": { input: 0.435, output: 0.87 },
    },
    features: [
      "reasoning",
      "chain_of_thought",
      "context_caching",
      "open_source",
      "batch_processing",
    ],
    // Context windows for the v4 models are not published alongside the
    // pricing; omitted rather than carried over from the retired models.
    specialFeatures: {
      "deepseek-v4-pro": [
        "chain_of_thought",
        "step_by_step_reasoning",
        "mathematical_problem_solving",
      ],
      "deepseek-v4-flash": [
        "general_purpose",
        "cost_efficient",
        "multilingual",
      ],
    },
  },

  groq: {
    label: "Groq Cloud",
    isLocal: false,
    apiKeyRequired: true,
    group: "more",
    costReporting: "estimated",
    endpoint: "https://api.groq.com/openai/v1",
    // The R1-distill, Llama 4 and Mistral Saba entries this list used to carry
    // are no longer in Groq's catalogue.
    models: [
      "llama-3.3-70b-versatile", // The default
      "llama-3.1-8b-instant", // Fastest
      "openai/gpt-oss-120b", // Open-weights GPT-OSS
      "openai/gpt-oss-20b", // Smaller GPT-OSS
      "qwen/qwen3.6-27b", // Qwen 3.6
    ],
    defaultModel: "llama-3.3-70b-versatile",
    description: "Ultra-fast inference with latest open models on LPU hardware",
    rates: {
      "llama-3.3-70b-versatile": { input: 0.59, output: 0.79 },
      "llama-3.1-8b-instant": { input: 0.05, output: 0.08 },
      "openai/gpt-oss-120b": { input: 0.15, output: 0.6 },
      "openai/gpt-oss-20b": { input: 0.075, output: 0.3 },
      "qwen/qwen3.6-27b": { input: 0.6, output: 3.0 },
    },
    features: [
      "ultra_fast_inference",
      "batch_processing",
      "open_models",
      "lpu_hardware",
    ],
    contextWindow: {
      "llama-3.3-70b-versatile": 128000,
      "llama-3.1-8b-instant": 128000,
      "openai/gpt-oss-120b": 128000,
      "openai/gpt-oss-20b": 128000,
      "qwen/qwen3.6-27b": 131072,
    },
    performanceMetrics: {
      "llama-3.1-8b-instant": "840 tokens/sec",
      "llama-3.3-70b-versatile": "394 tokens/sec",
    },
  },

  minimax: {
    label: "MiniMax",
    isLocal: false,
    apiKeyRequired: true,
    group: "more",
    costReporting: "estimated",
    endpoint: "https://api.minimax.io/anthropic",
    models: [
      "MiniMax-M3", // Latest flagship — multimodal, native tool use & thinking
      "MiniMax-M2.7", // Previous standard model
      "MiniMax-M2.7-highspeed", // Previous faster variant
      "MiniMax-M2.5", // Older generation
      "MiniMax-M2.5-highspeed", // Older gen faster variant
      "M2-her", // Conversational model
    ],
    defaultModel: "MiniMax-M3",
    description:
      "MiniMax-M3 flagship (multimodal, native tool use & thinking) with an Anthropic-compatible API — great value",
    rates: {
      // Base (≤512K context) tier; above 512K it is $0.60 / $2.40. MiniMax
      // publishes these as a standing 50%-off rate against a higher list price.
      "MiniMax-M3": { input: 0.3, output: 1.2 },
      "MiniMax-M2.7": { input: 0.3, output: 1.2 },
      "MiniMax-M2.7-highspeed": { input: 0.6, output: 2.4 },
      "MiniMax-M2.5": { input: 0.3, output: 1.2 },
      "MiniMax-M2.5-highspeed": { input: 0.6, output: 2.4 },
      "M2-her": { input: 0.3, output: 1.2 },
    },
    rateNotes: {
      "MiniMax-M3": "≤512K context; higher above",
    },
    features: ["anthropic_compatible", "fast_inference", "prompt_caching"],
    contextWindow: {
      "MiniMax-M3": 1000000, // confirm exact max; >512K pricing tier confirms long context
      "MiniMax-M2.7": 204800,
      "MiniMax-M2.7-highspeed": 204800,
      "MiniMax-M2.5": 204800,
      "MiniMax-M2.5-highspeed": 204800,
      "M2-her": 204800,
    },
    specialFeatures: {
      "MiniMax-M3": ["thinking", "tool_use", "long_context", "multimodal"],
    },
  },
};

// Provider dropdown grouping (issue #71). The set of supported providers is
// unchanged — this only controls what the user sees first. "recommended"
// leads with the provider that reports real cost (OpenRouter), the one with
// the best free tier (Google), and the one that costs nothing (Ollama).
export const PROVIDER_GROUPS = [
  { key: "recommended", label: "Recommended" },
  { key: "more", label: "More providers" },
];

export const getProviderGroups = (providers = LLM_PROVIDERS) => {
  return PROVIDER_GROUPS.map(({ key, label }) => ({
    key,
    label,
    providers: Object.entries(providers)
      .filter(([, config]) => (config.group || "more") === key)
      .map(([providerKey, config]) => ({ key: providerKey, ...config })),
  })).filter((group) => group.providers.length > 0);
};

// Helper function to get provider config
export const getProviderConfig = (provider) => {
  return LLM_PROVIDERS[provider] || null;
};

// Single source of truth for "which model do we use when the user hasn't
// picked one". Every fallback (`settings.model || ...`) should resolve through
// here so the default lives in exactly one place per provider.
export const getDefaultModel = (provider) => {
  return LLM_PROVIDERS[provider]?.defaultModel || null;
};

// Snap a stored model back onto something the provider still serves. Model
// catalogues move — Anthropic retired two models outright and DeepSeek
// deprecated both of its old ids — and a saved setting pointing at a withdrawn
// model fails at generation time with an opaque 404. Ollama is exempt: its
// models are whatever the user has pulled locally, so we can't validate them.
export const resolveKnownModel = (provider, model) => {
  const config = LLM_PROVIDERS[provider];
  if (!config || config.dynamicModels) return model;
  if (model && config.models.includes(model)) return model;
  return config.defaultModel || model;
};

// How trustworthy a cost figure for this provider is. Drives whether the UI
// says "$0.0021" or "~$0.0021 (est.)".
export const getCostReporting = (provider) => {
  return LLM_PROVIDERS[provider]?.costReporting || "estimated";
};

// USD per 1M tokens for a model, or null when we have no published rate.
export const getModelRates = (provider, model) => {
  const rates = LLM_PROVIDERS[provider]?.rates?.[model];
  if (!rates || !Number.isFinite(rates.input) || !Number.isFinite(rates.output))
    return null;
  return rates;
};

// Render a rate pair as the price string shown in the model dropdown.
// Derived from `rates` so the quoted price and the estimate always agree.
// With `money` (utils/money.js) the price is in the viewer's A$ or US$.
export const formatModelRate = (provider, model, money) => {
  const config = LLM_PROVIDERS[provider];
  if (!config) return null;
  const rates = getModelRates(provider, model);
  if (!rates) return null;

  const trim = (value) => (money ? formatPrice(value, money) : `$${Number(value.toFixed(4))}`);
  const parts = [`${trim(rates.input)} / 1M in`, `${trim(rates.output)} / 1M out`];
  const note = config.rateNotes?.[model];
  if (note) parts.push(note);

  const free = config.freeTier?.[model];
  const paid = parts.join(", ");
  return free ? `${free} + ${paid}` : paid;
};

// Updated API key validation patterns
export const validateApiKey = (provider, apiKey) => {
  if (!apiKey || apiKey.trim() === "") return false;

  const patterns = {
    // OpenAI's current keys are project, service-account or admin keys
    // (`sk-proj-…`, `sk-svcacct-…`) that carry hyphens and underscores. The
    // old letters-and-digits-only rule rejected every one of them.
    openai: /^sk-[a-zA-Z0-9_-]{40,}$/,
    // `sk-ant-api03-…`; the body is URL-safe base64, so `_` occurs too.
    anthropic: /^sk-ant-[a-zA-Z0-9_-]{40,}$/,
    // Two formats are live and both must pass. Legacy standard keys are
    // `AIza` + 35 chars. Keys minted in AI Studio since the auth-key
    // transition are bound to a service account and start `AQ.` — they carry
    // a dot and run longer, so the old exact-39-character rule rejected every
    // newly issued key and disabled the Save button with "Invalid API key
    // format". Kept deliberately loose: tight format policing here can only
    // ever lock a user out of a key that works, and "Test connection" is what
    // actually proves one.
    google: /^(AIza[a-zA-Z0-9_-]{35}|AQ\.[a-zA-Z0-9_.-]{20,})$/,
    groq: /^gsk_[a-zA-Z0-9]{52,}$/,
    deepseek: /^sk-[a-zA-Z0-9]{32,}$/, // DeepSeek API key pattern
    minimax: /^sk-api-[a-zA-Z0-9_-]{20,}$/, // MiniMax API key pattern
    openrouter: /^sk-or-v1-[a-zA-Z0-9]{32,}$/, // OpenRouter API key pattern
  };

  const pattern = patterns[provider];
  return pattern ? pattern.test(apiKey) : apiKey.length > 10;
};

// Enhanced model display name with context and performance info
export const getModelDisplayName = (provider, model, money) => {
  const config = LLM_PROVIDERS[provider];
  if (!config) return model;

  const context = config.contextWindow?.[model];
  const price = formatModelRate(provider, model, money);
  const performance = config.performanceMetrics?.[model];

  let displayName = model;

  // Add context window info
  if (context) {
    const contextStr =
      context >= 1000000
        ? `${Math.floor(context / 1000000)}M`
        : `${Math.floor(context / 1000)}K`;
    displayName += ` (${contextStr} context)`;
  }

  // Add performance info for Groq
  if (performance) {
    displayName += ` [${performance}]`;
  }

  // Add pricing info
  if (price) {
    displayName += ` - ${price}`;
  }

  return displayName;
};

// Updated feature checking
export const supportsFeature = (provider, model, feature) => {
  const config = LLM_PROVIDERS[provider];
  if (!config) return false;

  // Check general provider features
  const hasProviderFeature = config.features?.includes(feature);

  // Check model-specific features
  const hasModelFeature = config.specialFeatures?.[model]?.includes(feature);

  return hasProviderFeature || hasModelFeature || false;
};

// Legacy function for backward compatibility
export const supportsJsonMode = (provider, model) => {
  return supportsFeature(provider, model, "json_mode");
};

// Enhanced recommendation system
export const getRecommendedModel = (provider, useCase = "default") => {
  const config = LLM_PROVIDERS[provider];
  if (!config) return null;

  switch (useCase) {
    case "fast":
      // Return the fastest/cheapest model
      if (provider === "openai") return "gpt-6-luna";
      if (provider === "anthropic") return "claude-haiku-5-5";
      if (provider === "google") return "gemini-2.5-flash-lite";
      if (provider === "groq") return "llama-3.1-8b-instant";
      if (provider === "deepseek") return "deepseek-v4-flash";
      if (provider === "minimax") return "MiniMax-M2.7-highspeed";
      if (provider === "openrouter") return "deepseek/deepseek-v4-flash";
      return config.defaultModel;

    case "quality":
      // Return the highest quality model
      if (provider === "openai") return "gpt-6-astra";
      if (provider === "anthropic") return "claude-opus-5-5";
      if (provider === "google") return "gemini-3.8-flash";
      if (provider === "groq") return "openai/gpt-oss-120b";
      if (provider === "deepseek") return "deepseek-v4-pro";
      if (provider === "minimax") return "MiniMax-M3";
      if (provider === "openrouter") return "anthropic/claude-opus-5.5";
      return config.models[0];

    case "reasoning":
      // Return the best reasoning model
      if (provider === "openai") return "gpt-6-astra";
      if (provider === "anthropic") return "claude-opus-5-5";
      if (provider === "google") return "gemini-3.1-pro";
      if (provider === "groq") return "qwen/qwen3.6-27b";
      if (provider === "deepseek") return "deepseek-v4-pro";
      if (provider === "minimax") return "MiniMax-M3";
      if (provider === "openrouter") return "deepseek/deepseek-v4-pro";
      return config.defaultModel;

    case "coding":
      // Return the best coding model
      if (provider === "openai") return "gpt-6-astra";
      if (provider === "anthropic") return "claude-sonnet-5-5";
      if (provider === "google") return "gemini-3.8-flash";
      if (provider === "groq") return "openai/gpt-oss-120b";
      if (provider === "deepseek") return "deepseek-v4-pro";
      if (provider === "openrouter") return "anthropic/claude-sonnet-5.5";
      return config.defaultModel;

    case "long_context":
      // Return model with largest context window
      if (provider === "openai") return "gpt-6.1-sol";
      if (provider === "anthropic") return "claude-sonnet-5-5"; // 1M context
      if (provider === "google") return "gemini-3.8-flash"; // 1M context
      if (provider === "groq") return "qwen/qwen3.6-27b";
      if (provider === "deepseek") return "deepseek-v4-flash";
      if (provider === "openrouter") return "openai/gpt-6.1-sol";
      return config.defaultModel;

    case "cost_effective":
      // Return the most cost-effective model
      if (provider === "openai") return "gpt-6-luna";
      if (provider === "anthropic") return "claude-haiku-5-5";
      if (provider === "google") return "gemini-2.5-flash-lite";
      if (provider === "groq") return "llama-3.1-8b-instant";
      if (provider === "deepseek") return "deepseek-v4-flash";
      if (provider === "openrouter") return "deepseek/deepseek-v4-flash";
      return config.defaultModel;

    case "balanced":
    default:
      return config.defaultModel;
  }
};

// Get all models that support a specific feature
export const getModelsByFeature = (feature) => {
  const modelsByProvider = {};

  Object.entries(LLM_PROVIDERS).forEach(([provider, config]) => {
    const supportedModels = config.models.filter((model) =>
      supportsFeature(provider, model, feature),
    );

    if (supportedModels.length > 0) {
      modelsByProvider[provider] = supportedModels;
    }
  });

  return modelsByProvider;
};

// Get estimated cost for a request, in USD. Reads the structured `rates`
// table directly — no parsing of display strings.
export const estimateCost = (provider, model, inputTokens, outputTokens) => {
  const rates = getModelRates(provider, model);
  if (!rates) return null;

  const safeInput = Number.isFinite(inputTokens) ? Math.max(0, inputTokens) : 0;
  const safeOutput = Number.isFinite(outputTokens)
    ? Math.max(0, outputTokens)
    : 0;

  const inputCost = (safeInput / 1000000) * rates.input;
  const outputCost = (safeOutput / 1000000) * rates.output;

  return {
    inputCost,
    outputCost,
    totalCost: inputCost + outputCost,
    breakdown: {
      inputTokens: safeInput,
      outputTokens: safeOutput,
      inputPrice: rates.input,
      outputPrice: rates.output,
    },
  };
};
